import type {
  GoldExhibitionManufacturer,
  GoldExhibitionManufacturerInput,
  GoldExhibitionPurchaseRow,
  GoldExhibitionPurchaseRowRequest,
} from '@/lib/api/schemas/gold-exhibitions.schema'

export const MAX_GOLD_EXHIBITION_ROWS = 200
export const MAX_GRAMS = BigInt(1_000_000)
export const MAX_RUBLES_PER_GRAM = BigInt(1_000_000)

const KOPECKS_PER_RUBLE = BigInt(100)
const MILLIGRAMS_PER_GRAM = BigInt(1_000)

export interface GoldExhibitionEditorRow {
  id: string
  manufacturerText: string
  manufacturerId: string | null
  manufacturerCanonicalName: string | null
  gramsText: string
  rublesPerGramText: string
}

export interface GoldExhibitionPurchaseLine {
  id: string
  manufacturer: GoldExhibitionManufacturer
  weightMilliGrams: number | null
  priceKopecksPerGram: number | null
  amountKopecks: string | null
}

export type DecimalInputResult =
  | { status: 'empty' }
  | { status: 'invalid'; reason: 'format' | 'precision' | 'bound' }
  | { status: 'valid'; scaled: bigint }

export interface PurchaseTotals {
  weightMilliGrams: bigint
  kopecks: bigint
  incompleteRows: number
}

export type GoldExhibitionRowGroup =
  | { kind: 'registered'; manufacturerId: string; label: string; rows: GoldExhibitionPurchaseLine[] }
  | { kind: 'freeform'; label: string; rows: GoldExhibitionPurchaseLine[] }
  | { kind: 'unspecified'; label: 'Не указан'; rows: GoldExhibitionPurchaseLine[] }

export function createGoldExhibitionEditorRow(): GoldExhibitionEditorRow {
  return {
    id: globalThis.crypto.randomUUID(),
    manufacturerText: '',
    manufacturerId: null,
    manufacturerCanonicalName: null,
    gramsText: '',
    rublesPerGramText: '',
  }
}

function parseScaledDecimal(text: string, fractionDigits: number, maxInteger: bigint, maxTextLength: number): DecimalInputResult {
  const value = text.trim()
  if (!value) return { status: 'empty' }
  if (value.length > maxTextLength) return { status: 'invalid', reason: 'bound' }

  const match = /^(\d+)(?:[.,](\d+))?$/.exec(value)
  if (!match) return { status: 'invalid', reason: 'format' }

  const wholeText = match[1]
  const fractionalText = match[2] ?? ''
  if (fractionalText.length > fractionDigits) return { status: 'invalid', reason: 'precision' }

  let whole: bigint
  try {
    whole = BigInt(wholeText)
  } catch {
    return { status: 'invalid', reason: 'format' }
  }
  if (whole > maxInteger) return { status: 'invalid', reason: 'bound' }

  const scale = BigInt(10) ** BigInt(fractionDigits)
  const fractional = fractionalText ? BigInt(fractionalText.padEnd(fractionDigits, '0')) : BigInt(0)
  const scaled = whole * scale + fractional
  if (scaled > maxInteger * scale) return { status: 'invalid', reason: 'bound' }
  return { status: 'valid', scaled }
}

export function parseGramsInput(text: string): DecimalInputResult {
  return parseScaledDecimal(text, 3, MAX_GRAMS, 11)
}

export function parseRublesPerGramInput(text: string): DecimalInputResult {
  return parseScaledDecimal(text, 2, MAX_RUBLES_PER_GRAM, 10)
}

function rowTotalsFromEditor(row: Pick<GoldExhibitionEditorRow, 'gramsText' | 'rublesPerGramText'>): { weightMilliGrams: bigint; kopecks: bigint } | null {
  const grams = parseGramsInput(row.gramsText)
  const price = parseRublesPerGramInput(row.rublesPerGramText)
  if (grams.status !== 'valid' || price.status !== 'valid') return null

  const product = grams.scaled * price.scaled
  return {
    weightMilliGrams: grams.scaled,
    kopecks: (product + MILLIGRAMS_PER_GRAM / BigInt(2)) / MILLIGRAMS_PER_GRAM,
  }
}

export function calculateEditorRowKopecks(row: Pick<GoldExhibitionEditorRow, 'gramsText' | 'rublesPerGramText'>): bigint | null {
  return rowTotalsFromEditor(row)?.kopecks ?? null
}

export function calculateEditorTotals(rows: readonly GoldExhibitionEditorRow[]): PurchaseTotals {
  let weightMilliGrams = BigInt(0)
  let kopecks = BigInt(0)
  let incompleteRows = 0
  for (const row of rows) {
    const rowTotals = rowTotalsFromEditor(row)
    if (rowTotals === null) {
      incompleteRows += 1
    } else {
      weightMilliGrams += rowTotals.weightMilliGrams
      kopecks += rowTotals.kopecks
    }
  }
  return { weightMilliGrams, kopecks, incompleteRows }
}

export function calculatePurchaseRowKopecks(row: Pick<GoldExhibitionPurchaseLine, 'weightMilliGrams' | 'priceKopecksPerGram'>): bigint | null {
  if (row.weightMilliGrams === null || row.priceKopecksPerGram === null) return null
  const product = BigInt(row.weightMilliGrams) * BigInt(row.priceKopecksPerGram)
  return (product + MILLIGRAMS_PER_GRAM / BigInt(2)) / MILLIGRAMS_PER_GRAM
}

export function calculatePurchaseTotals(rows: readonly GoldExhibitionPurchaseLine[]): PurchaseTotals {
  let weightMilliGrams = BigInt(0)
  let kopecks = BigInt(0)
  let incompleteRows = 0
  for (const row of rows) {
    const rowKopecks = calculatePurchaseRowKopecks(row)
    if (rowKopecks === null || row.amountKopecks === null) {
      incompleteRows += 1
    } else {
      weightMilliGrams += BigInt(row.weightMilliGrams ?? 0)
      kopecks += BigInt(row.amountKopecks)
    }
  }
  return { weightMilliGrams, kopecks, incompleteRows }
}

function manufacturerInput(row: GoldExhibitionEditorRow): GoldExhibitionManufacturerInput {
  if (row.manufacturerId !== null && row.manufacturerCanonicalName !== null) {
    return { kind: 'selected', id: row.manufacturerId }
  }
  const text = row.manufacturerText.trim()
  return text ? { kind: 'freeform', text } : { kind: 'unspecified' }
}

export function editorRowRequest(row: GoldExhibitionEditorRow): GoldExhibitionPurchaseRowRequest | null {
  const grams = parseGramsInput(row.gramsText)
  const price = parseRublesPerGramInput(row.rublesPerGramText)
  if (grams.status === 'invalid' || price.status === 'invalid') return null

  return {
    manufacturer: manufacturerInput(row),
    weightMilliGrams: grams.status === 'valid' ? Number(grams.scaled) : null,
    priceKopecksPerGram: price.status === 'valid' ? Number(price.scaled) : null,
  }
}

function formatScaledInput(value: number | null, fractionDigits: number): string {
  if (value === null) return ''
  const scale = BigInt(10) ** BigInt(fractionDigits)
  const scaled = BigInt(value)
  const whole = scaled / scale
  const fraction = (scaled % scale).toString().padStart(fractionDigits, '0').replace(/0+$/, '')
  return fraction ? `${whole},${fraction}` : whole.toString()
}

export function editorRowFromPurchaseRow(row: GoldExhibitionPurchaseRow): GoldExhibitionEditorRow {
  const manufacturerText = row.manufacturer.kind === 'selected'
    ? row.manufacturer.displayName
    : row.manufacturer.kind === 'freeform'
      ? row.manufacturer.text
      : ''
  return {
    id: row.id,
    manufacturerText,
    manufacturerId: row.manufacturer.kind === 'selected' ? row.manufacturer.id : null,
    manufacturerCanonicalName: row.manufacturer.kind === 'selected' ? row.manufacturer.displayName : null,
    gramsText: formatScaledInput(row.weightMilliGrams, 3),
    rublesPerGramText: formatScaledInput(row.priceKopecksPerGram, 2),
  }
}

export function formatKopecks(kopecks: bigint): string {
  const whole = (kopecks / KOPECKS_PER_RUBLE).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
  const kopeckPart = (kopecks % KOPECKS_PER_RUBLE).toString().padStart(2, '0')
  return `${whole},${kopeckPart} ₽`
}

export function formatMilliGrams(weightMilliGrams: bigint): string {
  const whole = (weightMilliGrams / MILLIGRAMS_PER_GRAM).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
  const fraction = (weightMilliGrams % MILLIGRAMS_PER_GRAM).toString().padStart(3, '0')
  return `${whole},${fraction} г`
}

export function formatKopecksPerGram(kopecksPerGram: number): string {
  const value = BigInt(kopecksPerGram)
  const whole = (value / KOPECKS_PER_RUBLE).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
  const fraction = (value % KOPECKS_PER_RUBLE).toString().padStart(2, '0')
  return `${whole},${fraction} ₽/г`
}

export function groupGoldExhibitionRows(rows: readonly GoldExhibitionPurchaseLine[]): GoldExhibitionRowGroup[] {
  const registered = new Map<string, GoldExhibitionRowGroup & { kind: 'registered' }>()
  const freeform = new Map<string, GoldExhibitionPurchaseLine[]>()
  const unspecified: GoldExhibitionPurchaseLine[] = []

  for (const row of rows) {
    if (row.manufacturer.kind === 'selected') {
      const group = registered.get(row.manufacturer.id)
      if (group) {
        group.rows.push(row)
      } else {
        registered.set(row.manufacturer.id, {
          kind: 'registered',
          manufacturerId: row.manufacturer.id,
          label: row.manufacturer.displayName,
          rows: [row],
        })
      }
    } else if (row.manufacturer.kind === 'freeform') {
      const group = freeform.get(row.manufacturer.text)
      if (group) {
        group.push(row)
      } else {
        freeform.set(row.manufacturer.text, [row])
      }
    } else {
      unspecified.push(row)
    }
  }

  const groups: GoldExhibitionRowGroup[] = [...registered.values()]
  for (const [label, freeformRows] of freeform) groups.push({ kind: 'freeform', label, rows: freeformRows })
  if (unspecified.length > 0) groups.push({ kind: 'unspecified', label: 'Не указан', rows: unspecified })
  return groups
}
